/**
 * @file useCameraAvailable.ts
 * @description Доступна ли кнопка «Сканировать камерой» у поля скана
 *   (phase19.22 шаг 5): окружение браузера + правило `shouldShowCameraButton`.
 *
 * `enumerateDevices` до разрешения на камеру отдаёт устройства без названий,
 * но с видом — этого хватает, чтобы спрятать кнопку на устройстве без
 * камеры. Если камера всё же не нашлась при открытии (`NotFoundError`),
 * кнопка зовёт `markNoCamera()` при закрытии диалога (текст ошибки успевают
 * прочитать) — и кнопка пропадает у всех полей сразу.
 */

import { useEffect, useState, useSyncExternalStore } from "react";

import { shouldShowCameraButton, type CameraEnv, type CameraMode } from "./camera-scan";

/** Камеры нет — узнали при попытке открыть. Живёт до перезагрузки страницы. */
let noCamera = false;
const listeners = new Set<() => void>();

/** Отметить, что камеры на устройстве нет (скрыть кнопки). */
export function markNoCamera(): void {
  noCamera = true;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Основной указатель — палец: телефон/планшет (клавиатуру там не поднимаем). */
export function isCoarsePointer(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

/** Окружение браузера без учёта списка устройств. */
function readEnv(): Omit<CameraEnv, "hasVideoInput"> {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { hasGetUserMedia: false, coarsePointer: false };
  }
  return {
    hasGetUserMedia: typeof navigator.mediaDevices?.getUserMedia === "function",
    coarsePointer: isCoarsePointer(),
  };
}

export function useCameraAvailable(mode: CameraMode): boolean {
  const [hasVideoInput, setHasVideoInput] = useState<boolean | null>(null);
  const missing = useSyncExternalStore(subscribe, () => noCamera, () => false);
  const env = readEnv();
  const candidate = shouldShowCameraButton(mode, { ...env, hasVideoInput: null });

  // Список устройств спрашиваем, только если кнопка вообще может появиться.
  useEffect(() => {
    if (!candidate || typeof navigator.mediaDevices?.enumerateDevices !== "function") return;
    let cancelled = false;
    navigator.mediaDevices
      .enumerateDevices()
      .then((devices) => {
        if (!cancelled) setHasVideoInput(devices.some((d) => d.kind === "videoinput"));
      })
      .catch(() => {
        // Не узнали — оставляем кнопку: диалог сам скажет, если камеры нет.
      });
    return () => {
      cancelled = true;
    };
  }, [candidate]);

  return !missing && shouldShowCameraButton(mode, { ...env, hasVideoInput });
}
